import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Bot, Newspaper, CalendarClock, LineChart, Database,
  LogOut, Compass, Lock, Shield, Crown, Sparkles, TrendingUp, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasAccess, isAdmin } from "@/lib/access";
import { NotificationBell } from "@/components/NotificationBell";
import { PersistentLivePlayer } from "@/components/PersistentLivePlayer";
import { useLivePlayer } from "@/context/LivePlayerContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Radio } from "lucide-react";

function LiveButton() {
  const { open, openPlayer } = useLivePlayer();
  const { t } = useLang();
  return (
    <button onClick={openPlayer} data-testid="header-live-btn" title={t("live.marketContinuous")}
      className={`flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${open ? "border-red-700/60 bg-red-500/10 text-red-300" : "border-slate-800 bg-[#11141A] text-slate-400 hover:text-white"}`}>
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
      </span>
      <Radio className="h-3.5 w-3.5" /> {t("common.live")}
    </button>
  );
}

const NAV = [
  { to: "/dashboard", tkey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, minPlan: "basic" },
  { to: "/macro-overview", tkey: "nav.overview", icon: Compass, testid: "nav-overview", minPlan: "premium" },
  { to: "/agent", tkey: "nav.agent", icon: Bot, testid: "nav-agent", minPlan: "premium" },
  { to: "/news", tkey: "nav.news", icon: Newspaper, testid: "nav-news", minPlan: "basic" },
  { to: "/calendrier", tkey: "nav.calendar", icon: CalendarClock, testid: "nav-calendar", minPlan: "free" },
  { to: "/macro", tkey: "nav.macro", icon: LineChart, testid: "nav-macro", minPlan: "premium" },
  { to: "/tendances", tkey: "nav.trends", icon: TrendingUp, testid: "nav-trends", minPlan: "premium" },
  { to: "/donnees", tkey: "nav.explorer", icon: Database, testid: "nav-explorer", minPlan: "premium" },
];

const PLAN_BADGE = {
  premium: "bg-amber-500/15 text-amber-300",
  basic: "bg-blue-500/15 text-blue-300",
  free: "bg-slate-700/50 text-slate-400",
};

function NavItems({ user, navigate, onNavigate }) {
  const { t } = useLang();
  return (
    <>
      {NAV.map((n) => {
        const allowed = hasAccess(user, n.minPlan);
        if (allowed) {
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600/15 text-blue-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                }`}>
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t(n.tkey)}
            </NavLink>
          );
        }
        return (
          <button key={n.to} data-testid={`${n.testid}-locked`} onClick={() => { navigate("/tarifs"); onNavigate?.(); }}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-800/40 hover:text-slate-400">
            <n.icon className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t(n.tkey)}
            <Lock className="ml-auto h-3.5 w-3.5" />
          </button>
        );
      })}
    </>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const plan = user?.plan || "free";

  return (
    <div className="min-h-screen bg-[#0A0C10] text-slate-200">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-slate-800 bg-[#0B0E14] lg:flex">
        <div className="flex items-center gap-2.5 border-b border-slate-800 px-5 py-4">
          <img src="/logo.png" alt="Golden Terminal" className="h-9 w-9 object-contain" />
          <div>
            <div className="font-heading text-sm font-bold leading-none text-white">GOLDEN</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80">Terminal</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          <NavItems user={user} navigate={navigate} />
          {admin && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) =>
                `mt-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-purple-600/15 text-purple-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                }`}>
              <Shield className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t("nav.admin")}
            </NavLink>
          )}
        </nav>

        <div className="space-y-2 border-t border-slate-800 p-3">
          <NavLink to="/compte" data-testid="nav-account"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600/15 text-blue-300" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
              }`}>
            <CreditCard className="h-[18px] w-[18px]" strokeWidth={1.8} /> {t("nav.account")}
          </NavLink>
          {plan !== "premium" && (
            <button onClick={() => navigate("/tarifs")} data-testid="upgrade-btn"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-2 text-xs font-bold text-black transition-opacity hover:opacity-90">
              <Crown className="h-3.5 w-3.5" /> {t("nav.upgrade")}
            </button>
          )}
          <div className="pt-1"><LanguageSwitcher /></div>

          {user && (
            <div className="rounded-md border border-slate-800 bg-[#0E1118] p-2.5" data-testid="user-menu">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-white">{user.name}</div>
                  <div className="truncate text-[10px] text-slate-500">{user.email}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${PLAN_BADGE[plan]}`}>
                  {plan === "premium" ? <Crown className="h-3 w-3" /> : plan === "basic" ? <Sparkles className="h-3 w-3" /> : null}
                  {t(`plan.${plan}`)}
                </span>
                <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-white">
                  <LogOut className="h-3 w-3" /> {t("nav.logout")}
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 hidden items-center justify-end gap-2 border-b border-slate-800 bg-[#0A0C10]/90 px-6 py-3 backdrop-blur lg:flex">
          <LanguageSwitcher />
          <LiveButton />
          <NotificationBell />
        </header>
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-[#0A0C10]/90 px-4 py-3 backdrop-blur sm:px-6 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Golden Terminal" className="h-6 w-6 object-contain" />
            <span className="font-heading font-bold text-white">GOLDEN</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <LiveButton />
            <NotificationBell />
            {user && <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${PLAN_BADGE[plan]}`}>{t(`plan.${plan}`)}</span>}
            <button onClick={logout} data-testid="logout-btn-mobile" className="text-slate-400"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-slate-800 px-2 py-2 lg:hidden">
          {NAV.map((n) => {
            const allowed = hasAccess(user, n.minPlan);
            return allowed ? (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-blue-600/15 text-blue-300" : "text-slate-400"}`}>
                {t(n.tkey)}
              </NavLink>
            ) : (
              <button key={n.to} onClick={() => navigate("/tarifs")}
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-slate-600">
                {t(n.tkey)} <Lock className="h-3 w-3" />
              </button>
            );
          })}
          {admin && <NavLink to="/admin" className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-purple-600/15 text-purple-300" : "text-slate-400"}`}>{t("nav.admin")}</NavLink>}
        </nav>
        <main className="min-h-screen p-4 sm:p-6">{children}</main>
      </div>
      <PersistentLivePlayer />
    </div>
  );
}
